import { useEffect, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { resolveNavFeatureAccess } from '../../utils/resolveNavFeatureAccess';
import {
  buildDefaultNavAccess,
  isPlatformAdministrator,
  mergeNavFeatureAccess,
  type AppNavAccess,
} from './appNavAccess';
import { APP_NAV_FEATURE_CODES } from './appNavFeatureCodes';

/** Subscription + platform-admin resolved nav access (shared by SideNav and artifact tools). */
export function useAppNavAccess(): AppNavAccess {
  const { user, selectedCompany, availableCompanies } = useUser();
  const [featureAccess, setFeatureAccess] = useState<AppNavAccess>(() => buildDefaultNavAccess(user));

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const membership = availableCompanies.find((row) => row.company.id === selectedCompany?.id);
      const base = buildDefaultNavAccess(user, { membershipRole: membership?.instance.role });
      if (!user?.id) {
        if (!cancelled) setFeatureAccess(base);
        return;
      }

      if (isPlatformAdministrator(user)) {
        if (!cancelled) setFeatureAccess(base);
        return;
      }

      try {
        const codes = Object.values(APP_NAV_FEATURE_CODES);
        const results = await resolveNavFeatureAccess(user.id, codes, selectedCompany?.id);
        const merged = mergeNavFeatureAccess(base, results);
        if (!cancelled) setFeatureAccess(merged);
      } catch {
        if (!cancelled) setFeatureAccess(base);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [user, selectedCompany?.id, availableCompanies]);

  return featureAccess;
}
